import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams }  from 'react-router-dom';
import { Button } from '../components/Button';
import CompetitionCard from '../components/CompetitionCard';
import useAxios from '../api/axios';
import { toast } from 'react-toastify';
import useAuth from '../hooks/useAuth';
// import GetTheme from '../MuiTheme';



function Competitions ({selectView=false}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { competitionID: routeCompetitionID } = useParams();
  const api = useAxios();
  const { auth } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [registeredMap, setRegisteredMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [expandedID, setExpandedID] = useState(null);
  const [canCreateCompetition, setCanCreateCompetition] = useState(false);

  const user = auth?.user;
  const userID = user?.userID ?? null;
  const isUserAuthenticated = Boolean(user);
  const normalizedRouteCompetitionID = useMemo(() => {
    const numericID = Number(routeCompetitionID);
    return Number.isInteger(numericID) && numericID > 0 ? numericID : null;
  }, [routeCompetitionID]);

  const normalizeRegistrationValue = (item, fallbackClassID = null) => ({
    classID: item !== null && typeof item === 'object'
      ? (item.classID ?? fallbackClassID ?? null)
      : (fallbackClassID ?? null),
    paymentReferenceNumber: item !== null && typeof item === 'object'
      ? String(item.paymentReferenceNumber || '').trim()
      : '',
  });

  useEffect(() => {
    setExpandedID(normalizedRouteCompetitionID);
  }, [normalizedRouteCompetitionID]);

  const handleToggleExpand = (competitionID) => {
    const normalizedCompetitionID = Number(competitionID);

    if (!Number.isInteger(normalizedCompetitionID) || normalizedCompetitionID <= 0) {
      return;
    }

    const nextExpandedID = expandedID === normalizedCompetitionID ? null : normalizedCompetitionID;
    setExpandedID(nextExpandedID);
    navigate(nextExpandedID === null ? '/kisat' : `/kisat/${normalizedCompetitionID}`);
  };

  const handleCloseExpandedCompetition = () => {
    setExpandedID(null);
    navigate('/kisat');
  };

  useEffect(() => {
    if (!isUserAuthenticated) {
      setCanCreateCompetition(false);
      return;
    }

    let isMounted = true;

    const loadCreatePermission = async () => {
      try {
        const response = await api.post(
          '/getOrganizers.php',
          JSON.stringify({}),
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!isMounted) {
          return;
        }

        const organizers = Array.isArray(response?.data?.organizers) ? response.data.organizers : [];
        setCanCreateCompetition(organizers.some((organizer) => organizer?.permissions?.canCreateCompetitions));
      } catch {
        if (isMounted) {
          setCanCreateCompetition(false);
        }
      }
    };

    loadCreatePermission();

    return () => {
      isMounted = false;
    };
  }, [api, auth?.accessToken, isUserAuthenticated]);

  useEffect(() => {
    let isMounted = true;

    const parseCompetitions = (data) => {
      if (Array.isArray(data)) {
        return data;
      }

      if (Array.isArray(data?.competitions)) {
        return data.competitions;
      }

      if (Array.isArray(data?.companies)) {
        return data.companies;
      }

      return [];
    };

    const loadCompetitions = async () => {
      try {
        const response = await api.post(
          '/getCompetitions',
          JSON.stringify({}),
          { headers: { 'Content-Type': 'application/json' } }
        );

        const list = parseCompetitions(response?.data);

        if (!isMounted) {
          return;
        }

        setCompetitions(list);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const message = err?.response?.data?.message || err?.response?.data?.msg;
        toast.error(message || 'Kilpailujen haku epäonnistui');
        setCompetitions([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCompetitions();

    return () => {
      isMounted = false;
    };
  }, [api]);

  // Fetch registrations separately, re-run only when userID changes
  useEffect(() => {
    if (!userID) {
      setRegisteredMap(new Map());
      return;
    }

    let isMounted = true;

    const loadMyRegistrations = async () => {
      try {
        const response = await api.post(
          '/getMyRegistrations',
          JSON.stringify({}),
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (isMounted && Array.isArray(response?.data)) {
          const map = new Map();
          response.data.forEach((item) => {
            if (item !== null && typeof item === 'object') {
              map.set(Number(item.competitionID), normalizeRegistrationValue(item));
            } else {
              // legacy format: plain integer
              map.set(Number(item), normalizeRegistrationValue(null));
            }
          });
          setRegisteredMap(map);
        }
      } catch {
        // non-critical – ignore errors
      }
    };

    loadMyRegistrations();

    return () => {
      isMounted = false;
    };
  }, [api, userID]);

  const handleRegister = async (competitionID, classID = null) => {
    try {
      const response = await api.post(
        '/registerToCompetition',
        JSON.stringify({ competitionID, ...(classID !== null ? { classID } : {}) }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      setRegisteredMap((prev) => new Map([
        ...prev,
        [Number(competitionID), normalizeRegistrationValue(response?.data?.registration, classID)],
      ]));
      toast.success('Rekisteröinti onnistui!');
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      if (status === 409) {
        // Already registered – update local state so button disappears
        setRegisteredMap((prev) => new Map([...prev, [Number(competitionID), normalizeRegistrationValue(null, classID)]]));
      }
      toast.error(message || 'Rekisteröinti epäonnistui');
    }
  };

  const handleCancelRegistration = async (competitionID) => {
    try {
      await api.post(
        '/cancelRegistration',
        JSON.stringify({ competitionID }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      setRegisteredMap((prev) => {
        const next = new Map(prev);
        next.delete(Number(competitionID));
        return next;
      });
      toast.success('Rekisteröinti poistettu.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Rekisteröinnin poistaminen epäonnistui');
    }
  };

  const handleUpdateRegistration = async (competitionID, classID = null) => {
    try {
      const response = await api.post(
        '/updateRegistration',
        JSON.stringify({ competitionID, ...(classID !== null ? { classID } : {}) }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      setRegisteredMap((prev) => new Map([
        ...prev,
        [Number(competitionID), normalizeRegistrationValue(response?.data?.registration, classID)],
      ]));
      toast.success('Rekisteröinti päivitetty.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Rekisteröinnin päivittäminen epäonnistui');
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-10 pt-24">
      <div className='mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-center md:justify-between'>
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Kilpailut</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">
            {isUserAuthenticated ? '' : 'Kirjaudu rekisteröityäksesi kilpailuun'}
          </h1>          
        </div>

        {!selectView && (
          <div className="mt-4 md:mt-0">
            {isUserAuthenticated && canCreateCompetition ? (
              <Button name="Lisää uusi" type="PRIMARY" action={() => navigate('/lisaa_kisa')} />
            ) : !isUserAuthenticated ? (
              <Button
                name="Kirjaudu"
                type="PRIMARY"
                action={() => navigate('/login', { state: { from: location } })}
              />
            ) : null}
          </div>
        )}
      </div>

      {loading ? (
        <div className='rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300'>
          <p className="text-lg font-semibold text-white">Ladataan kilpailuja...</p>
        </div>
      ) :  (
        <div className="space-y-4">
          { competitions.map(comp => {
              const cid = Number(comp.competitionID);
              return (
                <CompetitionCard
                  competition={comp}
                  key={cid || comp.companyID || comp.id || comp.name}
                  canEditCompetition={canCreateCompetition}
                  isRegistered={registeredMap.has(cid)}
                  registeredClassID={registeredMap.get(cid)?.classID ?? null}
                  registrationPaymentReferenceNumber={registeredMap.get(cid)?.paymentReferenceNumber ?? ''}
                  isExpanded={expandedID === cid}
                  onExpand={() => handleToggleExpand(cid)}
                  onClose={handleCloseExpandedCompetition}
                  onRegister={handleRegister}
                  onCancelRegistration={handleCancelRegistration}
                  onUpdateRegistration={handleUpdateRegistration}
                />
              );
            })
          }
        </div>
      ) }
    </div>
  );
};

export default Competitions;